import { ValidateFields } from 'rc-field-form/es/interface';
import React, { useCallback, useMemo } from 'react';
import { IItemForGetShortInfoWithStatus, ITotalPrice, EntityStatus } from 'types';
import RestaurantsMultipleSelect from './components/RestaurantsMultipleSelect/RestaurantsMultipleSelect';
import PriceField from './components/PriceField';
import AddNewItemBtn from './components/AddNewFieldButton';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantPricesField.module.scss';

interface IRestaurantPricesField {
	addPrices: (value: ITotalPrice[]) => void;
	removePrice: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfoWithStatus[]) => void;
	setRestaurantsForPrice: (value: string[]) => void;
	changePrice: (value: string) => void;
	restaurantsForPrice: string[];
	totalPrices: ITotalPrice[];
	price: string;
	selectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	revalidateForm: ValidateFields;
}

const RestaurantPricesField: React.FC<IRestaurantPricesField> = ({
	restaurantsForPrice,
	totalPrices,
	price,
	selectedCustomer,
	fetchedRestaurants,
	removePrice,
	setRestaurantsForPrice,
	addPrices,
	setFetchedRestaurants,
	changePrice,
	revalidateForm,
}) => {
	const disabledButton = !restaurantsForPrice.length || !price;

	const handleAddPrice = useCallback(() => {
		const prices: ITotalPrice[] = restaurantsForPrice.map((el) => {
			const fetchedRestaurant = fetchedRestaurants.find((rest) => rest.id === el);
			return {
				restaurantId: el,
				restaurantName: fetchedRestaurant?.name || '',
				restaurantStatus: fetchedRestaurant?.status || EntityStatus.Active,
				price,
			};
		});
		prices.forEach((el) => {
			if (!totalPrices.some((total) => total.restaurantId === el.restaurantId)) {
				addPrices([el]);
				revalidateForm();
			}
		});
	}, [restaurantsForPrice, fetchedRestaurants, totalPrices, addPrices, price]);

	const pricesList = useMemo(() => {
		return totalPrices.map((el) => (
			<SelectedItem
				key={el.restaurantId}
				label={[el.restaurantName, el.price]}
				onDelete={() => removePrice([el.restaurantId])}
			/>
		));
	}, [totalPrices, removePrice]);

	return (
		<div className={style.container} data-test="RestaurantPricesField">
			<div className={style.field}>
				<RestaurantsMultipleSelect
					selectedCustomer={selectedCustomer}
					restaurantsForPrice={restaurantsForPrice}
					totalPrices={totalPrices}
					setRestaurantsForPrice={setRestaurantsForPrice}
					removePrice={removePrice}
					setFetchedRestaurants={setFetchedRestaurants}
				/>
				<PriceField restaurantsForPrice={restaurantsForPrice} changePrice={changePrice} />
				<AddNewItemBtn onClick={handleAddPrice} isDisabled={disabledButton} />
			</div>
			<div className={style.pricesList}>{pricesList}</div>
		</div>
	);
};

export default React.memo(RestaurantPricesField);
